import React from 'react';
import { ReactSortable } from 'react-sortablejs';
import { CircularProgress } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  useCampaignMutations,
  useCampaignPhases,
  useFetchCampaign,
  useFetchCampaignSteps,
  useReorderSteps,
} from '../../../../hooks/campaign-hooks';
import {
  Campaign,
  CampaignStatus,
  CampaignStep,
  CampaignStepNode,
  Json,
  buildStepTree,
  byOrder,
  nextOrder,
  stepKindLabel,
  statusLabel,
} from '../../../../support/campaigns';
import {
  BuilderRoot,
  GhostButton,
  PrimaryButton,
  StatusPill,
  StatusTone,
  builderStyles,
} from '../builder/primitives';
import CampaignMetaForm from '../builder/CampaignMetaForm';
import PromptsEditor from '../builder/PromptsEditor';
import AssetsEditor from '../builder/AssetsEditor';
import { TokenFieldProvider } from '../builder/TokenFieldContext';
import ContentBlocksEditor from '../builder/ContentBlocksEditor';
import StepDetailForm from '../builder/StepDetailForm';
import { CollapsibleSection } from '../builder/Disclosure';
import GuidePanel from '../builder/ai/GuidePanel';
import { persistStubTree } from '../builder/persistSteps';
import { StepStub } from '../ai/campaignFlows';
import s from './canvas.module.css';

/**
 * The Campaign Canvas — the visual-flow admin and the sole campaign builder (it replaced the former
 * stacked accordion editor at parity). `CampaignBuilder` routes here whenever a campaign is open.
 *
 * The board reads the campaign's phases (seeded `CampaignPhase` rows) as horizontal lanes and drops
 * each TOP-LEVEL step into its lane as a node-card; a step's sub-steps render recursively inside the
 * card (a nested drag list that reorders sub-steps and re-parents between groups). A click selects the
 * step and docks the existing `StepDetailForm` on the right — including its Parent picker for
 * cross-level moves — so nothing about how a step is authored is re-invented here. Campaign-level
 * settings (meta, phases, outcomes, capabilities, personas, prompts, content, assets) open in the same
 * right dock, reusing the builder's editors verbatim.
 *
 * Phase source (generic, DB-first): a lane is a seeded `CampaignPhase`; a step joins the lane whose key
 * matches `metadata.phase`. When a campaign has no seeded phases (or a step carries no explicit phase),
 * the runner-parity fallbacks below derive a lane from `metadata.runnerKind` so Frontier AI renders its
 * three-phase pipeline exactly as the runner does. Anything that still can't be placed lands in a
 * trailing "Unassigned" lane rather than vanishing.
 *
 * Two layout modes: the horizontal phase pipeline above is used only when phase lanes actually carry
 * steps (`usePipeline`). A phase-less campaign — arbitrary top-level groups with nested sub-steps and no
 * phase/runnerKind — instead renders as a single full-width vertical **tree** of its top-level steps
 * (the same node cards, one unified `canvas-nest` drag group spanning the root list and every group's
 * child list, so a step drags freely between levels including in/out of the top level).
 */

const CAMPAIGN_TONE: Record<CampaignStatus, StatusTone> = {
  draft: 'idle',
  published: 'done',
  archived: 'warn',
};

/** Runner-parity fallback: which phase key a Frontier `metadata.runnerKind` step belongs to. */
const RUNNER_KIND_PHASE: Record<string, string> = {
  profiling: 'pre-workshop',
  prepare: 'pre-workshop',
  propose: 'pre-workshop',
  workshop: 'workshop',
  deepdive: 'post-workshop',
  recommendation: 'post-workshop',
};

/** Human labels + order for the runner's bundled phase keys, used only when no phases are seeded. */
const FALLBACK_PHASE_LABEL: Record<string, string> = {
  'pre-workshop': 'Pre-Workshop',
  workshop: 'Workshop',
  'post-workshop': 'Post-Workshop',
};
const FALLBACK_PHASE_ORDER = ['pre-workshop', 'workshop', 'post-workshop'];

const UNASSIGNED = '__unassigned';

/** Title-case a bare phase key for a lane heading when nothing friendlier is available. */
function humanizeKey(key: string): string {
  return key
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/** The phase key a step belongs to: explicit `metadata.phase`, else derived from `metadata.runnerKind`. */
function phaseOf(step: CampaignStep): string | null {
  const md = (step.metadata ?? {}) as Json;
  if (typeof md.phase === 'string' && md.phase.trim()) return md.phase.trim();
  const rk = typeof md.runnerKind === 'string' ? md.runnerKind : '';
  if (rk && RUNNER_KIND_PHASE[rk]) return RUNNER_KIND_PHASE[rk];
  return null;
}

/** The short uppercase kind tag on a node — the Frontier runner phase when set, else the step kind. */
function nodeKindLabel(step: CampaignStep): string {
  const rk = (step.metadata as Json | undefined)?.runnerKind;
  if (typeof rk === 'string' && rk.trim()) return `Frontier · ${rk}`;
  return stepKindLabel(step.step_kind);
}

interface Lane {
  key: string;
  label: string;
  /** The lane's TOP-LEVEL nodes (each carrying its own sub-step subtree in `.children`). */
  steps: CampaignStepNode[];
}

/**
 * Build the ordered lanes (seeded phases first, else runner-parity fallback) and place every TOP-LEVEL
 * step. Only roots go into lanes; a root's sub-steps ride inside its node card (rendered recursively),
 * so a phase pipeline of top-level steps and an arbitrarily nested step tree coexist on one board.
 */
function buildLanes(
  phases: { key: string; label: string }[],
  roots: CampaignStepNode[]
): Lane[] {
  const placed = new Map<string, CampaignStepNode[]>();
  const add = (key: string, node: CampaignStepNode) => {
    const list = placed.get(key) ?? [];
    list.push(node);
    placed.set(key, list);
  };

  // The lane keys, in order. Seeded phases win; otherwise synthesize from what the steps reference.
  let laneKeys: { key: string; label: string }[];
  if (phases.length) {
    laneKeys = phases.map((p) => ({ key: p.key, label: p.label || humanizeKey(p.key) }));
  } else {
    const seen = new Set<string>();
    roots.forEach((node) => {
      const key = phaseOf(node);
      if (key) seen.add(key);
    });
    const ordered = [
      ...FALLBACK_PHASE_ORDER.filter((k) => seen.has(k)),
      ...[...seen].filter((k) => !FALLBACK_PHASE_ORDER.includes(k)).sort(),
    ];
    laneKeys = ordered.map((key) => ({ key, label: FALLBACK_PHASE_LABEL[key] || humanizeKey(key) }));
  }

  const known = new Set(laneKeys.map((l) => l.key));
  roots.forEach((node) => {
    const key = phaseOf(node);
    add(key && known.has(key) ? key : UNASSIGNED, node);
  });

  const lanes: Lane[] = laneKeys.map((l) => ({
    key: l.key,
    label: l.label,
    steps: byOrder(placed.get(l.key) ?? []) as CampaignStepNode[],
  }));

  const unassigned = placed.get(UNASSIGNED);
  if (unassigned && unassigned.length) {
    lanes.push({ key: UNASSIGNED, label: 'Unassigned', steps: byOrder(unassigned) as CampaignStepNode[] });
  }
  return lanes;
}

export function CampaignCanvas(props: {
  campaignId: number;
  canWrite: boolean;
  selectedStepId: number | null;
  onSelectStep: (id: number | null) => void;
  onBack: () => void;
}): JSX.Element {
  const { campaignId, canWrite, selectedStepId, onSelectStep, onBack } = props;

  const [guideOpen, setGuideOpen] = React.useState(false);
  const [settingsOpen, setSettingsOpen] = React.useState(false);
  // Defined = the step dock is in CREATE mode under this parent (null = a root step); `undefined` = the
  // create dock is closed. Editing an existing step is driven by selectedStepId (mirrors the shell).
  const [createParent, setCreateParent] = React.useState<number | null | undefined>(undefined);

  const { data: campaign, isLoading } = useFetchCampaign(campaignId);
  const { data: steps = [] } = useFetchCampaignSteps(campaignId);
  const { phases } = useCampaignPhases(campaignId);
  const campaignMutations = useCampaignMutations('campaign');
  const stepMutations = useCampaignMutations('step');
  const reorder = useReorderSteps();

  // Optimistic mirror of the fetched steps, re-seeded on content change (not just a same-rows
  // re-fetch), so dragging a node within a lane updates the board immediately and the `#order`
  // badges stay consistent across lanes while the reorder PUTs settle.
  const [localSteps, setLocalSteps] = React.useState<CampaignStep[]>(steps);
  const stepsSig = React.useMemo(() => JSON.stringify(steps), [steps]);
  React.useEffect(() => {
    setLocalSteps(steps);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepsSig]);
  const localRef = React.useRef(localSteps);
  localRef.current = localSteps;

  const selectedStep = React.useMemo(
    () => steps.find((st) => st.id === selectedStepId) ?? null,
    [steps, selectedStepId]
  );

  // The full step tree (roots + nested subtrees) drives the board: roots go into phase lanes, each
  // root's sub-steps render recursively inside its card. Built off the optimistic `localSteps` so a
  // drag reflows immediately.
  const tree = React.useMemo(() => buildStepTree(localSteps), [localSteps]);
  const lanes = React.useMemo(() => buildLanes(phases, tree), [phases, tree]);

  // Two layout modes. A campaign whose steps actually populate phase lanes (Frontier AI) renders the
  // horizontal phase pipeline. A phase-less campaign — top-level groups with nested sub-steps and no
  // `metadata.phase`/`runnerKind` — has no phase lane carrying a step (everything would fall into the
  // trailing Unassigned lane beside empty phase lanes), so it renders instead as a single full-width
  // vertical tree of its top-level steps. This keeps the pipeline for the shape it was built for while
  // giving arbitrary nested campaigns a clean tree — the shape the deployed builder showed.
  const usePipeline = React.useMemo(
    () => lanes.some((l) => l.key !== UNASSIGNED && l.steps.length > 0),
    [lanes]
  );

  // Apply a lane's new visual order optimistically. A lane groups steps by phase, so its members may
  // be siblings under different parents (for Frontier, all under root); for each affected parent we
  // splice the lane's steps back into the positions they occupied among that parent's full children,
  // then re-index `order` 0..n. This preserves the relative order of steps that live in other lanes.
  const applyLaneOrder = React.useCallback(
    (newVisual: CampaignStep[]): CampaignStep[] => {
      const byParent = new Map<number | null, CampaignStep[]>();
      newVisual.forEach((st) => {
        const p = st.parent ?? null;
        const list = byParent.get(p) ?? [];
        list.push(st);
        byParent.set(p, list);
      });
      const source = localRef.current;
      const nextById = new Map(source.map((st) => [st.id, st]));
      byParent.forEach((laneSteps, parentId) => {
        const full = byOrder(source.filter((st) => (st.parent ?? null) === parentId));
        const laneIds = new Set(laneSteps.map((st) => st.id));
        const positions: number[] = [];
        full.forEach((st, i) => {
          if (laneIds.has(st.id)) positions.push(i);
        });
        const rebuilt = [...full];
        positions.forEach((pos, k) => {
          const src = nextById.get(laneSteps[k].id);
          if (src) rebuilt[pos] = src;
        });
        rebuilt.forEach((st, i) => nextById.set(st.id, { ...st, order: i }));
      });
      return Array.from(nextById.values());
    },
    []
  );

  const onLaneList = React.useCallback(
    (newVisual: CampaignStep[]) => {
      if (!canWrite) return;
      setLocalSteps(applyLaneOrder(newVisual));
    },
    [canWrite, applyLaneOrder]
  );

  // Persist the optimistic order once the drag ends: hand useReorderSteps the *original* server rows
  // arranged in the new visual order, per parent, so it can see the delta and PUT only what changed.
  const persistOrder = React.useCallback(() => {
    if (!canWrite) return;
    const current = localRef.current;
    const originalById = new Map(steps.map((st) => [st.id, st]));
    const parentIds = new Set<number | null>([null]);
    current.forEach((st) => parentIds.add(st.parent ?? null));
    const groups = Array.from(parentIds).map((parentId) => ({
      parentId,
      ordered: byOrder(current.filter((st) => (st.parent ?? null) === parentId)).map(
        (st) => originalById.get(st.id) ?? st
      ),
    }));
    reorder.mutate(groups);
  }, [canWrite, steps, reorder]);

  // A nested (sub-step) list changed: every item in the list now belongs to `parentId` at its list
  // index — reorder within a group and re-parent between groups both flow through here (each nested
  // list owns exactly one parent, so there is no cross-lane ambiguity). `parentId` is `null` for the
  // tree-mode root list, which lets a step be dragged in/out of a group and re-homed to the top level.
  const handleNestList = React.useCallback(
    (parentId: number | null, newList: CampaignStep[]) => {
      if (!canWrite) return;
      setLocalSteps((prev) => {
        const byId = new Map(prev.map((st) => [st.id, st]));
        newList.forEach((item, index) => {
          const existing = byId.get(item.id);
          if (existing) byId.set(item.id, { ...existing, parent: parentId, order: index });
        });
        return Array.from(byId.values());
      });
    },
    [canWrite]
  );

  // Delete a node from a card (parity with the tree's inline delete). Sub-steps are not cascaded — the
  // runtime re-homes them to the top level — so the confirm mirrors StepDetailForm.onDelete.
  const deleteStep = React.useCallback(
    (node: CampaignStepNode) => {
      if (!canWrite) return;
      const childCount = node.children?.length ?? 0;
      const message =
        childCount > 0
          ? `Delete this step? Its ${childCount} sub-step${
              childCount === 1 ? '' : 's'
            } will move to the top level (they are not deleted).`
          : 'Delete this step?';
      // eslint-disable-next-line no-alert
      if (!window.confirm(message)) return;
      stepMutations.remove.mutate(node.id);
      if (node.id === selectedStepId) onSelectStep(null);
    },
    [canWrite, stepMutations.remove, selectedStepId, onSelectStep]
  );

  const applyEdit = React.useCallback(
    (target: 'campaign' | string, field: string, value: string | number | boolean) => {
      if (target === 'campaign') {
        if (!campaign) return;
        campaignMutations.update.mutate({ ...campaign, [field]: value, id: campaign.id });
        return;
      }
      if (typeof target === 'string' && target.startsWith('step:')) {
        const sid = Number(target.slice('step:'.length));
        const st = steps.find((x) => x.id === sid);
        if (st) stepMutations.update.mutate({ ...st, [field]: value, id: st.id });
      }
    },
    [campaign, steps, campaignMutations.update, stepMutations.update]
  );

  const addSteps = React.useCallback(
    async (stubs: StepStub[]) => {
      if (!campaign || !stubs?.length) return;
      await persistStubTree(
        stepMutations.create.mutateAsync,
        campaign.id,
        stubs,
        null,
        nextOrder(steps, null)
      );
    },
    [campaign, steps, stepMutations.create.mutateAsync]
  );

  const openCreate = React.useCallback(
    (parentId: number | null) => {
      setSettingsOpen(false);
      onSelectStep(null);
      setCreateParent(parentId);
    },
    [onSelectStep]
  );

  const selectStep = React.useCallback(
    (id: number | null) => {
      setSettingsOpen(false);
      setCreateParent(undefined);
      onSelectStep(id);
    },
    [onSelectStep]
  );

  const closeDock = React.useCallback(() => {
    setCreateParent(undefined);
    setSettingsOpen(false);
    onSelectStep(null);
  }, [onSelectStep]);

  const openSettings = React.useCallback(() => {
    setCreateParent(undefined);
    onSelectStep(null);
    setSettingsOpen(true);
  }, [onSelectStep]);

  const isCreating = createParent !== undefined;
  const stepDockOpen = isCreating || selectedStep != null;

  if (isLoading || !campaign) {
    return (
      <BuilderRoot>
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <CircularProgress />
        </div>
      </BuilderRoot>
    );
  }

  // A nested sub-step list only accepts a drop when the drop target is a group's child list (tagged
  // `cb-accepts-children`), so a step can never nest under a non-group leaf.
  const nestPut = (to: { el?: { classList?: DOMTokenList } }): boolean =>
    !!to?.el?.classList?.contains('cb-accepts-children');

  // A group node's children, as a nested react-sortablejs list sharing the `canvas-nest` group so
  // sub-steps reorder within a group and re-parent between groups. Always rendered for a group (even
  // when empty) so there is a drop target and a ＋ affordance.
  const renderChildren = (parentId: number, children: CampaignStepNode[]): JSX.Element => (
    <ReactSortable
      list={children.map((c) => ({ ...c }))}
      setList={(next) => handleNestList(parentId, next as unknown as CampaignStep[])}
      onEnd={persistOrder}
      group={{ name: 'canvas-nest', put: nestPut, pull: true }}
      handle=".cb-canvas-node-drag"
      animation={150}
      disabled={!canWrite || reorder.isPending}
      className={`${s.childList} cb-accepts-children`}
    >
      {children.map((child) => renderNode(child))}
    </ReactSortable>
  );

  // A single node card, recursively rendering its subtree. Used for both a lane's top-level nodes and
  // (via renderChildren) every nested sub-step.
  const renderNode = (node: CampaignStepNode): JSX.Element => {
    const isGroup = node.step_kind === 'group';
    const childCount = node.children.length;
    return (
      <div key={node.id} className={s.nodeWrap}>
        <div
          role="button"
          tabIndex={0}
          className={`${s.node} ${node.id === selectedStepId ? s.nodeSelected : ''} ${
            node.hidden ? s.nodeHidden : ''
          }`}
          onClick={() => selectStep(node.id)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              selectStep(node.id);
            }
          }}
        >
          <div className={s.nodeHead}>
            {canWrite && (
              <span
                className={`${s.nodeDrag} cb-canvas-node-drag`}
                onClick={(e) => e.stopPropagation()}
                aria-hidden
              >
                <DragIndicatorIcon sx={{ fontSize: 16 }} />
              </span>
            )}
            <span className={s.nodeKind}>{nodeKindLabel(node)}</span>
            <span className={s.nodeOrder}>#{node.order}</span>
            {canWrite && (
              <span className={s.nodeActions}>
                {isGroup && (
                  <button
                    type="button"
                    className={s.nodeBtn}
                    title="Add sub-step"
                    aria-label="Add sub-step"
                    onClick={(e) => {
                      e.stopPropagation();
                      openCreate(node.id);
                    }}
                  >
                    ＋
                  </button>
                )}
                <button
                  type="button"
                  className={s.nodeBtnDanger}
                  title="Delete step"
                  aria-label="Delete step"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteStep(node);
                  }}
                >
                  ×
                </button>
              </span>
            )}
          </div>
          <p className={s.nodeTitle}>{node.name || 'Untitled step'}</p>
          <div className={s.nodeMeta}>
            {node.is_required && <span className={s.metaTag}>Required</span>}
            {node.blocks_progression && <span className={s.metaTag}>Blocks</span>}
            {node.hidden && <span className={`${s.metaTag} ${s.metaTagWarn}`}>Hidden</span>}
            {isGroup && (
              <span className={s.metaTag}>
                {childCount} sub-step{childCount === 1 ? '' : 's'}
              </span>
            )}
          </div>
        </div>
        {isGroup && renderChildren(node.id, node.children)}
      </div>
    );
  };

  return (
    <BuilderRoot>
      <div className={s.canvasRoot}>
        <div className={s.bar}>
          <div className={s.barMain}>
            <GhostButton onClick={onBack}>
              <ArrowBackIcon sx={{ fontSize: 16 }} />
              All campaigns
            </GhostButton>
            <h2 className={s.title}>{campaign.name}</h2>
            <StatusPill tone={CAMPAIGN_TONE[campaign.status] ?? 'idle'}>
              {statusLabel(campaign.status)}
            </StatusPill>
          </div>
          <div className={s.barActions}>
            <GhostButton onClick={openSettings}>⚙ Settings</GhostButton>
            {canWrite && (
              <PrimaryButton onClick={() => openCreate(null)}>＋ Add step</PrimaryButton>
            )}
            <GhostButton onClick={() => setGuideOpen(true)}>✨ AI Guide</GhostButton>
          </div>
        </div>

        {usePipeline ? (
        <div className={s.board}>
          {lanes.map((lane, laneIdx) => (
            <React.Fragment key={lane.key}>
              {laneIdx > 0 && (
                <div className={s.laneGap} aria-hidden>
                  →
                </div>
              )}
              <div className={s.lane}>
                <div
                  className={`${s.laneHead} ${
                    lane.key === UNASSIGNED ? s.laneHeadUnassigned : ''
                  }`}
                >
                  <span className={s.laneLabel}>{lane.label}</span>
                  <span className={s.laneCount}>{lane.steps.length}</span>
                </div>
                <div className={s.laneBody}>
                  {lane.steps.length === 0 ? (
                    <div className={s.emptyLane}>No steps in this phase</div>
                  ) : (
                    <ReactSortable
                      list={lane.steps.map((st) => ({ ...st }))}
                      setList={(next) => onLaneList(next as CampaignStep[])}
                      onEnd={persistOrder}
                      group={`canvas-lane-${lane.key}`}
                      handle=".cb-canvas-node-drag"
                      animation={150}
                      disabled={!canWrite || reorder.isPending}
                      className={s.nodeList}
                    >
                      {lane.steps.map((node) => renderNode(node))}
                    </ReactSortable>
                  )}
                  {canWrite && (
                    <button type="button" className={s.addNode} onClick={() => openCreate(null)}>
                      ＋ Add step
                    </button>
                  )}
                </div>
              </div>
            </React.Fragment>
          ))}
        </div>
        ) : (
          <div className={s.tree}>
            {tree.length === 0 ? (
              <div className={s.emptyLane}>No steps yet — add the campaign&apos;s first step.</div>
            ) : (
              <ReactSortable
                list={tree.map((n) => ({ ...n }))}
                setList={(next) => handleNestList(null, next as unknown as CampaignStep[])}
                onEnd={persistOrder}
                group={{ name: 'canvas-nest', put: nestPut, pull: true }}
                handle=".cb-canvas-node-drag"
                animation={150}
                disabled={!canWrite || reorder.isPending}
                className={`${s.treeRoot} cb-accepts-children`}
              >
                {tree.map((node) => renderNode(node))}
              </ReactSortable>
            )}
            {canWrite && (
              <button type="button" className={s.addNode} onClick={() => openCreate(null)}>
                ＋ Add step
              </button>
            )}
          </div>
        )}
      </div>

      {(stepDockOpen || settingsOpen) && (
        <>
          <div className={s.dockScrim} onClick={closeDock} aria-hidden />
          <div className={s.dock} role="dialog" aria-modal="true">
            <div className={s.dockHead}>
              <h3 className={s.dockTitle}>
                {settingsOpen
                  ? 'Campaign settings'
                  : isCreating
                    ? 'New step'
                    : selectedStep?.name || 'Step'}
              </h3>
              <button type="button" className={s.dockClose} onClick={closeDock} aria-label="Close">
                ×
              </button>
            </div>
            <div className={s.dockBody}>
              {settingsOpen ? (
                <div className={s.dockStack}>
                  <CollapsibleSection title="Campaign details" subtitle="Name, status, and core settings." accent defaultOpen>
                    <CampaignMetaForm
                      key={campaign.id}
                      campaign={campaign}
                      canWrite={canWrite}
                      onDone={onBack}
                    />
                  </CollapsibleSection>
                  <CollapsibleSection title="AI prompts" subtitle="Stored prompts the runner and report use.">
                    {/* AI prompts run per delivery (grounded in the live customer/opportunity + captured
                        answers), so their token picker offers the delivery-scoped groups too. */}
                    <TokenFieldProvider scope="delivery">
                      <PromptsEditor campaign={campaign} canWrite={canWrite} />
                    </TokenFieldProvider>
                  </CollapsibleSection>
                  <CollapsibleSection
                    title="Workshop Guide nudges"
                    subtitle="Campaign-wide guidance lines the Workshop Guide surfaces during a run (welcome, idle, per-step, per-phase). Each block's key names the slot it fires in."
                  >
                    <ContentBlocksEditor
                      campaign={campaign}
                      canWrite={canWrite}
                      only={['nudge']}
                      categoryLabels={{ nudge: 'Workshop Guide nudges' }}
                      title="Workshop Guide nudges"
                      subtitle='Key each line by its slot — e.g. "welcome", "idle", "intro:profiling", "phasedone:discovery".'
                    />
                  </CollapsibleSection>
                  <CollapsibleSection
                    title="Assets"
                    subtitle="Decks, PDFs, images, and docs the campaign references by key — including the Word report-template file (key report-template) exports splice into."
                  >
                    <AssetsEditor campaign={campaign} canWrite={canWrite} />
                  </CollapsibleSection>
                </div>
              ) : (
                <StepDetailForm
                  key={isCreating ? `new-${String(createParent)}` : selectedStep!.id}
                  campaign={campaign}
                  step={isCreating ? undefined : selectedStep!}
                  parentId={isCreating ? createParent : undefined}
                  allSteps={steps}
                  canWrite={canWrite}
                  onSaved={closeDock}
                  onDeleted={closeDock}
                  onCancel={closeDock}
                />
              )}
            </div>
          </div>
        </>
      )}

      {guideOpen && (
        <>
          <div className={builderStyles.guideScrim} onClick={() => setGuideOpen(false)} aria-hidden />
          <div className={builderStyles.guideDrawer}>
            <GuidePanel
              campaign={campaign}
              steps={steps}
              selectedStep={selectedStep}
              onApplyEdit={applyEdit}
              onAddSteps={addSteps}
              onClose={() => setGuideOpen(false)}
            />
          </div>
        </>
      )}
    </BuilderRoot>
  );
}

export default CampaignCanvas;
